<template>
  <span
    v-tooltip="'Select page'"
    class="relative inline-flex items-center"
  >
    <button
      :id="triggerId"
      type="button"
      :disabled="disabled"
      data-test-id="am3-page-select-trigger"
      class="inline-flex items-center border-t-2 border-transparent px-4 py-4 text-sm font-medium text-secondary hover:border-surface-weak hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-transparent disabled:hover:text-secondary"
      @click="toggle"
    >
      <slot>...</slot>
    </button>

    <AM3DropdownPanel
      v-model="isOpen"
      :target="triggerId"
      position="top"
      :fit-content="true"
    >
      <div class="p-3 flex items-center gap-2">
        <input
          ref="inputRef"
          v-model.number="inputPage"
          :placeholder="currentPage"
          type="number"
          :min="1"
          :max="totalPages"
          step="1"
          data-test-id="am3-page-select-input"
          class="w-20 px-3 py-2 text-sm rounded-md border border-surface-dark bg-surface-light outline-none focus:outline-brand focus:outline-offset-0 focus:outline-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          @keydown.enter="confirm"
          @focus="($event.target as HTMLInputElement).select()"
        >
        <AM3Button
          type="primary"
          data-test-id="am3-page-select-confirm"
          :disabled="!isValidPage"
          text="Go"
          aria-label="Go to page"
          @click="confirm"
        /></div>
    </AM3DropdownPanel>
  </span>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import AM3DropdownPanel from '@/components/am3-generic/base/AM3DropdownPanel.vue'

const props = withDefaults(defineProps<{
  totalPages: number
  currentPage: number
  disabled?: boolean
}>(), {
  disabled: false,
})

const emit = defineEmits<{
  select: [page: number]
}>()

const triggerId = `am3-page-select-${useId()}`
const isOpen = ref(false)
const inputPage = ref<number | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)

const isValidPage = computed(() =>
  inputPage.value !== null
  && Number.isInteger(inputPage.value)
  && inputPage.value >= 1
  && inputPage.value <= props.totalPages,
)

function toggle() {
  if (props.disabled) return
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    inputPage.value = null
    nextTick(() => inputRef.value?.focus())
  }
}

function confirm() {
  if (!isValidPage.value) return
  emit('select', inputPage.value as number)
  isOpen.value = false
  inputPage.value = null
}
</script>
